/**
 * /admin/match-health/:matchId — Tier 2 diagnostic page.
 *
 * Single-shot view of everything the coder + ops need to confirm a live
 * recording is actually flowing:
 *   - active MatchRecording state (UPLOADING / COMPLETE / ABORTED) + finalize phase
 *   - chunks landed in S3 under live-chunks/<rec_id>/ (count, total bytes, last seen)
 *   - per-ball checklist: does ball-clips/YYYY/MM/DD/<ball>.mp4 exist yet?
 *   - commentary length sanity (auto-generated text should be > 0 chars)
 *
 * Auto-refreshes every 8 s while the match is LIVE so ops can watch a
 * scoring session in real time. The "Download JSON" button hits the
 * /bundle/ sibling endpoint so support can paste the full dump into a
 * ticket / Slack thread.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CheckCircle2, AlertCircle, Loader2, Download, RefreshCw, Film, Video,
} from "lucide-react";
import { getMatchHealth, downloadMatchHealthBundleUrl } from "../../api/recordings.api";


function fmtBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}


function StatePill({ label, ok, value, tone = "auto" }) {
  // `tone` lets the caller override the OK/error mapping (e.g. a chunk
  // count of 0 isn't an error if recording just started).
  let cls = "bg-bg-secondary/40 border-border-default text-text-secondary";
  if (tone === "ok" || (tone === "auto" && ok)) {
    cls = "bg-emerald-500/10 border-emerald-500/30 text-emerald-300";
  } else if (tone === "err" || (tone === "auto" && ok === false)) {
    cls = "bg-rose-500/10 border-rose-500/30 text-rose-300";
  } else if (tone === "warn") {
    cls = "bg-amber-500/10 border-amber-500/30 text-amber-300";
  }
  return (
    <div className={`px-3 py-2 rounded border text-xs ${cls}`}>
      <div className="uppercase tracking-wider text-[10px] opacity-70">{label}</div>
      <div className="text-sm font-mono mt-0.5">{value}</div>
    </div>
  );
}


export default function MatchHealth() {
  const { matchId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [auto, setAuto] = useState(true);
  const refreshRef = useRef(null);

  const load = async (initial = false) => {
    if (initial) setLoading(true);
    setErr("");
    try {
      const d = await getMatchHealth(matchId);
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load match health");
    } finally {
      if (initial) setLoading(false);
    }
  };

  useEffect(() => { load(true); }, [matchId]);

  useEffect(() => {
    if (!auto) return undefined;
    refreshRef.current = setInterval(() => load(false), 8_000);
    return () => clearInterval(refreshRef.current);
  }, [auto, matchId]);

  const isLive = (data?.match?.state || "").toUpperCase() === "LIVE";
  const balls = data?.balls || [];
  const missing = balls.filter((b) => !b.has_clip && !b.is_dead_ball);
  const missingByInnings = useMemo(() => {
    const m = {};
    for (const b of missing) m[b.innings] = (m[b.innings] || 0) + 1;
    return m;
  }, [missing]);

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-text-secondary">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading match health…
      </div>
    );
  }

  if (err && !data) {
    return (
      <div className="p-8 text-rose-400 flex items-center gap-2">
        <AlertCircle className="w-4 h-4" /> {err}
      </div>
    );
  }

  const rec = data.recording;

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-4">
      <header className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Video className="w-5 h-5" /> Match Health
            {isLive && (
              <span className="text-[10px] px-2 py-0.5 rounded bg-rose-500/20 text-rose-300 uppercase tracking-wider">
                LIVE
              </span>
            )}
          </h1>
          <div className="text-xs text-text-secondary font-mono mt-1">
            Match {data.match.id} · {data.match.state} · generated {fmtTime(data.generated_at)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-text-secondary flex items-center gap-1">
            <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
            auto-refresh
          </label>
          <button
            onClick={() => load(false)}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-bg-secondary border border-border-default hover:bg-bg-secondary/60"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          <a
            href={downloadMatchHealthBundleUrl(matchId)}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-blue-500/20 border border-blue-500/30 text-blue-200 hover:bg-blue-500/30"
            download
          >
            <Download className="w-3 h-3" /> JSON bundle
          </a>
        </div>
      </header>

      {err && (
        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2">
          {err}
        </div>
      )}

      {/* Top stat strip — at-a-glance OK/error.
          T219: explicit grid-cols-1 base so pills don't squish on phones. */}
      <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        <StatePill
          label="Recording"
          value={rec ? rec.state : "NONE"}
          tone={!rec ? "err" : rec.state === "UPLOADING" ? "ok" : "auto"}
          ok={!!rec}
        />
        <StatePill
          label="Recorder v"
          value={rec ? `v${rec.recorder_version}` : "—"}
          tone={rec?.recorder_version === 2 ? "ok" : "warn"}
        />
        <StatePill
          label="Chunks in S3"
          value={`${data.chunks.count.toLocaleString()}`}
          tone={data.chunks.count > 0 ? "ok" : rec ? "warn" : "err"}
        />
        <StatePill
          label="Total bytes"
          value={fmtBytes(data.chunks.total_bytes)}
        />
        <StatePill
          label="Balls coded"
          value={data.match.balls_total.toLocaleString()}
        />
        <StatePill
          label="Missing clips"
          value={data.match.missing_clips.toLocaleString()}
          tone={data.match.missing_clips === 0 ? "ok" : "warn"}
        />
      </section>

      {/* Recording detail card */}
      <section className="bg-bg-secondary/30 border border-border-default rounded p-3 text-xs">
        <div className="uppercase tracking-wider text-[10px] text-text-secondary mb-2 flex items-center gap-2">
          <Film className="w-3 h-3" /> Recording
        </div>
        {!rec ? (
          <div className="text-rose-300">No MatchRecording row for this match. Operator hasn't clicked Start Recording yet.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1 font-mono">
            <div><span className="text-text-secondary">id:</span> {rec.id}</div>
            <div><span className="text-text-secondary">state:</span> {rec.state}</div>
            <div><span className="text-text-secondary">tier:</span> {rec.tier}</div>
            <div><span className="text-text-secondary">codec:</span> {rec.codec}</div>
            <div><span className="text-text-secondary">duration:</span> {(rec.duration_ms / 1000).toFixed(1)}s</div>
            <div><span className="text-text-secondary">byte_size:</span> {fmtBytes(rec.byte_size)}</div>
            <div><span className="text-text-secondary">started:</span> {fmtTime(rec.started_at)}</div>
            <div><span className="text-text-secondary">completed:</span> {fmtTime(rec.completed_at)}</div>
            <div><span className="text-text-secondary">finalize_phase:</span> {rec.finalize_phase}</div>
            <div><span className="text-text-secondary">finalize_pct:</span> {rec.finalize_progress_pct}%</div>
            <div className="col-span-2"><span className="text-text-secondary">s3_key:</span> {rec.s3_key}</div>
            {rec.finalize_error && (
              <div className="col-span-full text-rose-300">finalize_error: {rec.finalize_error}</div>
            )}
          </div>
        )}
      </section>

      {/* Chunks summary */}
      <section className="bg-bg-secondary/30 border border-border-default rounded p-3 text-xs">
        <div className="uppercase tracking-wider text-[10px] text-text-secondary mb-2">S3 chunks</div>
        <div className="font-mono grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1">
          <div><span className="text-text-secondary">prefix:</span> {data.chunks.prefix || "—"}</div>
          <div><span className="text-text-secondary">count:</span> {data.chunks.count}</div>
          <div><span className="text-text-secondary">total_bytes:</span> {fmtBytes(data.chunks.total_bytes)}</div>
          <div><span className="text-text-secondary">last_seen:</span> {fmtTime(data.chunks.last_seen_at)}</div>
        </div>
      </section>

      {/* Per-innings missing-clip summary */}
      {Object.keys(missingByInnings).length > 0 && (
        <section className="bg-amber-500/10 border border-amber-500/30 rounded p-3 text-xs text-amber-200">
          <div className="uppercase tracking-wider text-[10px] mb-1">Missing clips by innings</div>
          <div className="font-mono flex flex-wrap gap-3">
            {Object.entries(missingByInnings).map(([i, n]) => (
              <span key={i}>Innings {i}: {n}</span>
            ))}
          </div>
        </section>
      )}

      {/* Per-ball table */}
      <section className="bg-bg-secondary/30 border border-border-default rounded">
        <div className="px-3 py-2 uppercase tracking-wider text-[10px] text-text-secondary border-b border-border-default flex items-center justify-between">
          <span>Per-ball status ({balls.length} balls)</span>
          <span className="font-mono normal-case">
            {balls.filter((b) => b.has_clip).length} with clip · {missing.length} missing
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-text-secondary uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left px-2 py-1">Inn</th>
                <th className="text-left px-2 py-1">Ball</th>
                <th className="text-left px-2 py-1">Saved</th>
                <th className="text-left px-2 py-1">Clip</th>
                <th className="text-left px-2 py-1">Clip size</th>
                <th className="text-left px-2 py-1">Clip uploaded</th>
                <th className="text-left px-2 py-1">Comm.</th>
                <th className="text-left px-2 py-1">Ball ID</th>
              </tr>
            </thead>
            <tbody>
              {balls.map((b) => (
                <tr
                  key={b.ball_id}
                  className={`border-t border-border-default/40 ${
                    !b.has_clip && !b.is_dead_ball ? "bg-amber-500/5" : ""
                  }`}
                >
                  <td className="px-2 py-1 font-mono">{b.innings}</td>
                  <td className="px-2 py-1 font-mono">{b.label}</td>
                  <td className="px-2 py-1 font-mono text-text-secondary">
                    {fmtTime(b.saved_at)}
                  </td>
                  <td className="px-2 py-1">
                    {b.is_dead_ball ? (
                      <span className="text-text-secondary">dead</span>
                    ) : b.has_clip ? (
                      <span className="text-emerald-400 inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> yes
                      </span>
                    ) : (
                      <span className="text-amber-300 inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> missing
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1 font-mono">{fmtBytes(b.clip_size_bytes)}</td>
                  <td className="px-2 py-1 font-mono text-text-secondary">
                    {fmtTime(b.clip_uploaded_at)}
                  </td>
                  <td className="px-2 py-1 font-mono">
                    {b.commentary_len > 0 ? (
                      <span className="text-emerald-400">{b.commentary_len}c</span>
                    ) : (
                      <span className="text-rose-400">empty</span>
                    )}
                  </td>
                  <td className="px-2 py-1 font-mono text-[10px] text-text-secondary">
                    {b.ball_id.slice(0, 8)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
